import React from 'react';
import Context from './Context';
import { useQuery, useQueryClient } from 'react-query';
import { useUser } from '@ls/user-module';

const usePersistencyQuery = (defaultState, stateKey, appId, username) => {
  if (!stateKey?.length) {
    throw new Error('Missing stateKey');
  }
  const { baseURL } = React.useContext(Context);
  const queryClient = useQueryClient();
  const queryKey = React.useMemo(() => {
    return [baseURL, stateKey, appId, username];
  }, [baseURL, stateKey, appId, username]);

  const stateQuery = useQuery({
    placeholderData: defaultState,
    queryKey,
    queryFn: async () => {
      try {
        const url = new URL(`${baseURL}/setting`);
        url.search = new URLSearchParams({ appId, username, settingKey: stateKey }).toString();
        const response = await window.fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          mode: 'cors',
        });
        const persistedState = await response.json();
        if (persistedState === null && defaultState) {
          return defaultState;
        } else {
          return persistedState;
        }
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    cacheTime: 60 * 60 * 1000,
    staleTime: 60 * 60 * 1000,
    retryOnMount: true,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const setState = React.useCallback(async (state) => {
    try {
      const url = new URL(`${baseURL}/setting`);
      url.search = new URLSearchParams({ appId, username, settingKey: stateKey }).toString();
      await window.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(state),
      });
      queryClient.setQueryData(queryKey, state);
    } catch (e) {
      console.error(e);
    }
  }, [appId, baseURL, queryClient, queryKey, stateKey, username]);

  return [stateQuery, setState];
};

/** User Level State **/
export const usePersistentState = (defaultState, stateKey) => {
  const { appId } = React.useContext(Context);
  const { username } = useUser();
  return usePersistencyQuery(defaultState, stateKey, appId, username);
};